import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Zap, ArrowRight, Sparkles, Rocket, Crown, Flame } from "lucide-react";
import { capture } from "@/lib/analytics";

const PACK_THEMES = {
  credits_100:  { icon: Zap,      tagline: "Try it out",         accent: "#3B82F6", border: "rgba(59,130,246,0.30)"  },
  credits_300:  { icon: Sparkles, tagline: "Most popular",       accent: "#10B981", border: "rgba(16,185,129,0.45)", badge: "★ Most popular" },
  credits_750:  { icon: Rocket,   tagline: "Best value",         accent: "#F59E0B", border: "rgba(245,158,11,0.45)", badge: "💎 Best value" },
  credits_2000: { icon: Crown,    tagline: "Power user",         accent: "#7C3AED", border: "rgba(124,58,237,0.40)" },
};

const USE_CASES = [
  { name: "Custom AI Mentor Feedback",      cost: "10–20 credits" },
  { name: "Deep Challenge Analysis",        cost: "15 credits" },
  { name: "Personalized Challenge",         cost: "15 credits" },
  { name: "Personalized Prompt Pack",       cost: "25 credits" },
  { name: "Business Idea Deep Dive",        cost: "25 credits" },
  { name: "Sales Script Optimization",      cost: "20 credits" },
  { name: "Prompt Optimization",            cost: "15 credits" },
  { name: "Real Estate Deal Analysis",      cost: "30 credits" },
];

export default function PowerBoosts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loadingPack, setLoadingPack] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get("/credits/packs");
        setPacks(r.data?.packs || []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[power-boosts] pack fetch failed:", e?.message);
      }
      if (user) {
        try {
          const r = await apiClient.get("/credits/me");
          setBalance(r.data?.balance ?? 0);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[power-boosts] balance fetch failed:", e?.message);
        }
      }
    })();
  }, [user]);

  useEffect(() => {
    capture("power_boosts_viewed");
  }, []);

  const buyPack = async (slug) => {
    if (!user) {
      navigate("/login?returnTo=/power-boosts");
      return;
    }
    setError(null);
    setLoadingPack(slug);
    try {
      capture("credits_pack_started", { pack: slug });
      const r = await apiClient.post("/credits/checkout", {
        pack: slug,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || "Could not start checkout.");
      setLoadingPack(null);
    }
  };

  const isSubscriber = !!user?.has_subscription;

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 pb-28 md:pb-20">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <span className="kicker">AI Power Boosts</span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl mt-3 leading-[1.02] tracking-tight">
            Unlock deeper <em>AI experiences</em>.
          </h1>
          <p className="mt-5 text-[var(--ink-2)] text-base sm:text-lg leading-relaxed">
            Power-ups for moments when you want more — custom mentor feedback, deep analysis, personalized prompt packs.
            Use them when you need them.
          </p>
        </div>

        {/* Balance + subscriber callout */}
        {user && (
          <div
            className="brut p-5 mb-8 max-w-3xl mx-auto flex items-center justify-between gap-4 flex-wrap"
            data-testid="power-boosts-balance-card"
            style={{ background: "linear-gradient(135deg, rgba(251,191,36,0.08) 0%, rgba(245,158,11,0.04) 100%)" }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)" }}
              >
                <Zap size={20} color="#fff" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[var(--ink-3)]">Your AI Power</div>
                <div className="font-display text-3xl leading-tight" data-testid="power-boosts-balance">
                  {balance ?? "—"} <span className="text-base text-[var(--ink-2)] font-normal">credits</span>
                </div>
              </div>
            </div>
            {isSubscriber ? (
              <div className="text-sm text-[var(--ink-2)] flex items-center gap-2">
                <Sparkles size={14} className="text-[var(--emerald,#10B981)]" />
                <span><strong>150 monthly AI Power included</strong> with your Mastery Journey</span>
              </div>
            ) : (
              <Link to="/pricing" className="btn-ghost" data-testid="power-boosts-mastery-link">
                Get 150 / month with Mastery →
              </Link>
            )}
          </div>
        )}

        {/* Pack grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {packs.map((pack) => {
            const theme = PACK_THEMES[pack.slug] || PACK_THEMES.credits_100;
            const Icon = theme.icon;
            const perCredit = (pack.price / pack.credits).toFixed(3);
            return (
              <div
                key={pack.slug}
                className="brut bg-white p-6 relative flex flex-col"
                style={{ borderColor: theme.border }}
                data-testid={`pack-${pack.slug}-card`}
              >
                {theme.badge && (
                  <div
                    className="absolute -top-3 left-4 pill"
                    style={{
                      background: theme.accent,
                      color: "#fff",
                      borderColor: theme.accent,
                      fontSize: "0.65rem",
                      padding: "0.3rem 0.7rem",
                    }}
                  >
                    {theme.badge}
                  </div>
                )}

                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{ background: theme.accent }}
                >
                  <Icon size={20} color="#fff" strokeWidth={2.5} />
                </div>

                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-[var(--ink-3)]">{theme.tagline}</div>
                  <div className="font-display text-2xl mt-1 leading-tight">{pack.credits.toLocaleString()} credits</div>
                </div>

                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="font-display text-3xl">${pack.price.toFixed(2)}</span>
                </div>
                <div className="text-[10px] text-[var(--ink-3)] mt-0.5">
                  ${perCredit} per credit
                </div>

                <button
                  type="button"
                  onClick={() => buyPack(pack.slug)}
                  disabled={loadingPack === pack.slug}
                  className="btn-primary w-full mt-5 justify-center inline-flex items-center min-h-[44px]"
                  style={{ background: theme.accent, borderColor: theme.accent, fontSize: "0.85rem", padding: "0.6rem 1rem" }}
                  data-testid={`pack-${pack.slug}-buy`}
                >
                  {loadingPack === pack.slug ? <span className="spinner" /> : <>Get Power <ArrowRight size={13} /></>}
                </button>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="text-sm text-[var(--accent,#FF5C2A)] text-center mt-5" data-testid="power-boosts-error">{error}</div>
        )}

        {/* Use cases */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <span className="kicker">What credits unlock</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-2 leading-tight">Use them when you need them.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {USE_CASES.map((u) => (
              <div key={u.name} className="brut p-4 flex items-center justify-between gap-4">
                <div className="font-medium text-sm text-[var(--ink)]">{u.name}</div>
                <div className="text-xs text-[var(--ink-3)] flex-shrink-0 pl-3 border-l border-[var(--line)]">{u.cost}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-[var(--ink-3)] mt-6 leading-relaxed">
            Power-ups roll out progressively — your credits never expire. Subscribers' monthly 150 carry over for up to 90 days.
          </p>
        </div>

        {/* CTA back to mastery */}
        {!isSubscriber && (
          <div
            className="mt-14 brut-lg p-8 max-w-3xl mx-auto text-center"
            style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #F0F4FF 100%)" }}
          >
            <Flame size={26} className="mx-auto mb-3" style={{ color: "var(--accent,#FF5C2A)" }} />
            <h3 className="font-display text-2xl sm:text-3xl leading-tight">
              Want 150 credits every month, automatically?
            </h3>
            <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed max-w-xl mx-auto">
              The AI Mastery Journey includes 150 AI Power credits per month, unlimited daily challenges,
              and every belt path — for about the price of one coffee a week.
            </p>
            <Link to="/pricing" className="btn-duo mt-6 inline-flex" data-testid="power-boosts-cta-mastery">
              Continue Your Journey <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
